import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { apiCall, streamCall } from '../utils/api.js'
import { useAuthStore } from './auth.js'
import { isPrivilegedRole, canRoleOperateStatus } from '../utils/workflowAccess.js'
import { normalizeConditions } from '../utils/fieldRules.js'

export const useProjectStore = defineStore('projects', () => {
  // Estado
  const projects = ref([])
  const allProjects = ref([]) // Tutti i progetti senza filtro per i summary
  const deadlines = ref([]) // Deadline attive (endpoint dedicato, indipendente dalla paginazione)
  const loading = ref(false)
  const error = ref(null)
  const currentProject = ref(null)
  // Il processo aperto nel form, e se ha modifiche non ancora salvate. Finche' e' sporco, gli
  // aggiornamenti arrivati dall'hub toccano la riga in lista ma non il form: sovrascriverlo
  // cancellerebbe quello che l'utente sta scrivendo. Li governa ProjectForm.
  const openProjectId = ref(null)
  const currentProjectDirty = ref(false)
  // Chi ha modificato il processo aperto mentre lo stavamo editando: alimenta l'avviso nel form.
  const externalChange = ref(null)
  // Contatore degli archiviati: l'Archivio ha una lista propria (endpoint diverso) e si ricarica
  // guardando questo, invece di iscriversi da solo agli eventi dell'hub.
  const archiveTick = ref(0)
  const statusFilter = ref(null)
  const projectTypeFilter = ref(null)
  const groupFilter = ref(null)
  // Ambito per profilo. `onlyMine` a null significa "come da ruolo": acceso per chi opera solo
  // alcune fasi, spento per admin e superadmin, che le vedono tutte. `role` e' il filtro degli
  // admin: guarda la lista con gli occhi di un altro profilo, senza cambiare utente.
  const scopeFilter = ref({ onlyMine: null, role: null })
  const configurations = ref({
    config: null,
    fields: null,
    columns: null
  })

  // Paginação
  const currentPage = ref(1)
  const pageSize = ref(50)
  
  // Computed
  const totalProjects = computed(() => filteredProjects.value.length)

  // Profilo di chi sta guardando. Lo store di autenticazione si risolve qui dentro perche' Pinia
  // permette di comporre gli store solo dentro al setup.
  const authStore = useAuthStore()
  const currentRole = computed(() => authStore.user?.role || null)

  // Filtro "solo le mie fasi" effettivo: acceso di default per chi non e' admin.
  const onlyMyStatuses = computed(() =>
    scopeFilter.value.onlyMine ?? !isPrivilegedRole(currentRole.value))

  function canRoleOperateProject(role, project) {
    const info = getStatusInfo(String(project.activeStatus), parseInt(project.projectType))
    return canRoleOperateStatus(role, info)
  }

  // Processi filtrati per profilo, PRIMA dei filtri di tipo, stato e gruppo. Dashboard e tabella
  // partono entrambe da qui, cosi' contatori, pipeline ed elenco raccontano la stessa storia.
  const scopedProjects = computed(() => {
    // Filtro admin per utente: si guarda la lista con gli occhi di quel profilo
    if (scopeFilter.value.role) {
      return projects.value.filter(p => canRoleOperateProject(scopeFilter.value.role, p))
    }

    if (!onlyMyStatuses.value) return projects.value

    return projects.value.filter(p => canRoleOperateProject(currentRole.value, p))
  })

  const filteredProjects = computed(() => {
    // Se non è selezionato alcun tipo di progetto, non mostrare nulla
    if (projectTypeFilter.value === null || projectTypeFilter.value === undefined) {
      return []
    }

    let filtered = scopedProjects.value
    
    // Filtra per tipo di progetto
    filtered = filtered.filter(project => parseInt(project.projectType) === projectTypeFilter.value)
    
    // Filtra per status
    if (statusFilter.value !== null && statusFilter.value !== undefined) {
      filtered = filtered.filter(project => parseInt(project.activeStatus) === parseInt(statusFilter.value))
    } else if (groupFilter.value !== null && groupFilter.value !== undefined) {
      // Filtra per gruppo: prende tutti gli status del tipo corrente che appartengono al gruppo
      const statusListForType = projectTypes.value.find(pt => pt.id === projectTypeFilter.value)?.statusList || []
      const groupStatusIds = statusListForType
        .filter(s => s.group === groupFilter.value)
        .map(s => s.id)
      filtered = filtered.filter(project => groupStatusIds.includes(parseInt(project.activeStatus)))
    }
    
    return filtered
  })

  const statusList = computed(() => {
    // Se c'è un filtro per tipo di progetto, ritorna gli stati di quel tipo
    if (projectTypeFilter.value !== null) {
      const projectType = projectTypes.value.find(pt => pt.id === projectTypeFilter.value)
      return projectType?.statusList || []
    }
    // Altrimenti ritorna tutti gli stati di tutti i tipi (per compatibilità)
    const allStatuses = []
    const seenIds = new Set()
    projectTypes.value.forEach(pt => {
      pt.statusList?.forEach(status => {
        if (!seenIds.has(status.id)) {
          seenIds.add(status.id)
          allStatuses.push(status)
        }
      })
    })
    return allStatuses
  })

  const projectTypes = computed(() => {
    return configurations.value.config?.projectTypes || []
  })

  // Biblioteca delle azioni IA (cfg.json -> aiActions): la configurazione completa di ogni azione
  // vive qui una volta sola, e gli stati la richiamano per nome.
  const aiActions = computed(() => {
    return configurations.value.config?.aiActions || []
  })

  // Chiamate dei tool configurate (Classes/tools.json): { <tool>: { calls: { <call>: {...} } } }.
  // Il form ne legge il checkbox di esito delle azioni engine "tool".
  const toolsConfig = computed(() => configurations.value.tools || {})

  /**
   * Azioni IA di uno stato, con la definizione presa dalla biblioteca. Ogni voce dello stato è un
   * riferimento `{ ref, id?, enabled? }`: `ref` è l'azione, `id` (quando c'è) è l'id runtime del
   * pulsante, `enabled: false` la spegne solo in questo stato. Un riferimento rotto viene scartato.
   * Le definizioni scritte dentro allo stato (cfg.json non ancora migrato) passano invariate.
   */
  function resolveVerifications(status) {
    const list = status?.verifications
    if (!Array.isArray(list)) return []

    return list.map(entry => {
      if (!entry || typeof entry !== 'object') return null
      if (!entry.ref) return entry

      const action = aiActions.value.find(a => a.id === entry.ref)
      if (!action) {
        console.warn(`Ação de IA '${entry.ref}' não existe em aiActions`)
        return null
      }

      const resolved = { ...action, id: entry.id || entry.ref }
      // Azione su tool: il checkbox del gate lo decide il pannello del tool, non l'azione
      if (action.engine === 'tool') {
        const checkbox = toolsConfig.value?.[action.tool]?.calls?.[action.call]?.outcome?.checkboxField
        if (checkbox) resolved.validationField = checkbox
        else delete resolved.validationField
      }
      // Spenta nello stato o spenta nella biblioteca: in entrambi i casi il pulsante non compare
      if (entry.enabled === false || action.enabled === false) resolved.enabled = false
      else delete resolved.enabled
      // Condizioni di attivazione: quelle dello stato SOSTITUISCONO quelle della biblioteca (una
      // lista vuota dichiarata nello stato sblocca il pulsante proprio lì). Normalizzate qui una
      // volta sola: i consumatori ricevono sempre una lista.
      resolved.conditionalOn = normalizeConditions(
        Object.prototype.hasOwnProperty.call(entry, 'conditionalOn') ? entry.conditionalOn : action.conditionalOn)
      return resolved
    }).filter(Boolean)
  }

  // Profili operativi (cfg.json -> roles) e cosa succede fuori dalle proprie fasi.
  const workflowRoles = computed(() => {
    const list = configurations.value.config?.roles
    return Array.isArray(list) ? list : []
  })

  const roleAccessPolicy = computed(() =>
    configurations.value.config?.roleAccess?.outsideStatuses || 'readonly')

  const fields = computed(() => {
    return configurations.value.fields || {}
  })

  // Campi raggruppati per tipo di processo (Classes/fields/<id>.json). Usato solo dagli editor:
  // i consumatori in sola lettura continuano a usare la lista piatta `fields`.
  const fieldsByProjectType = computed(() => {
    return configurations.value.fieldsByProjectType || {}
  })

  const fieldRules = computed(() => {
    return configurations.value.fields || {}
  })

  const instructions = computed(() => {
    return configurations.value.instructions || {}
  })

  // Colonne della tabella dashboard (columns.json). null se il file manca o è malformato:
  // ProjectsTable ha un set di default interno per quel caso.
  const tableColumns = computed(() => {
    return configurations.value.columns || null
  })

  // Config strutturale cartelle (da Backend/config.json): nomi contenitori + template per tipo
  const folders = computed(() => {
    return configurations.value.folders || {}
  })

  // Unisce segmenti in un percorso Windows, normalizzando i separatori
  function joinPath(root, ...segs) {
    let base = String(root || '').replace(/[\\/]+$/, '')
    for (const s of segs) {
      if (s == null || String(s).length === 0) continue
      base += '\\' + String(s).replace(/^[\\/]+/, '').replace(/[\\/]+$/, '')
    }
    return base
  }

  // Legge la root configurata nel LocalBackend
  async function fetchRootPath() {
    const pathsConfig = await apiCall('/api/config/config', {
      method: 'GET',
      backend: 'local',
      includeAuth: false
    })
    return pathsConfig?.rootPath
  }

  // Percorsi effettivi: Processos e Arquivo sono fissi; il template è per-tipo
  const processesDir = (root) => joinPath(root, folders.value.processes)
  const archiveDir = (root) => joinPath(root, folders.value.archive)
  const templateDir = (root, projectType) => {
    const byType = folders.value.templatesByType || {}
    const name = byType[String(projectType)]
    return name ? joinPath(root, folders.value.templates, name) : ''
  }

  // Actions
  async function loadConfigurations() {
    try {
      loading.value = true
      const response = await apiCall('/api/projects/configs')
      configurations.value = response
    } catch (err) {
      error.value = 'Erro ao carregar configurações'
      console.error('Erro ao carregar configurações:', err)
    } finally {
      loading.value = false
    }
  }

  // Salva la definizione campi (Classes/fields/<id>.json) dall'editor in Impostazioni.
  // Solo superadmin lato Backend, che pretende la mappa completa dei tipi di processo esistenti.
  // Ricarica le configurazioni così il form usa subito la nuova definizione senza refresh della pagina.
  async function saveFieldsByType(byProjectType) {
    const result = await apiCall('/api/projects/configs/fields', {
      method: 'PUT',
      body: JSON.stringify({ byProjectType })
    })
    await loadConfigurations()
    return result
  }

  // Salva la configurazione colonne della tabella dashboard (columns.json). Solo superadmin lato Backend.
  // Ricarica le configurazioni così la tabella si aggiorna subito senza refresh della pagina.
  async function saveColumns(columnsConfig) {
    const result = await apiCall('/api/projects/configs/columns', {
      method: 'PUT',
      body: JSON.stringify(columnsConfig)
    })
    await loadConfigurations()
    return result
  }

  // Salva cfg.json: tipi di processo, stati e biblioteca delle azioni IA. Solo superadmin lato
  // Backend. Il file si scrive INTERO, quindi chi salva una sola metà deve rimandare l'altra com'è
  // sul server: `saveCfgParts` la recupera dalla configurazione caricata.
  // Ricarica le configurazioni così form, dashboard e tabella usano subito il nuovo workflow.
  async function saveCfg(cfgConfig) {
    const result = await apiCall('/api/projects/configs/cfg', {
      method: 'PUT',
      body: JSON.stringify(cfgConfig)
    })
    await loadConfigurations()
    return result
  }

  // Salva cfg.json rimpiazzando solo le parti passate: l'editor stati manda i `projectTypes`,
  // quello delle azioni manda gli `aiActions`, e l'altra metà resta quella dell'ultimo salvataggio.
  async function saveCfgParts(parts) {
    const current = configurations.value.config || {}
    const payload = {
      projectTypes: parts.projectTypes ?? current.projectTypes ?? [],
      aiActions: parts.aiActions ?? current.aiActions ?? [],
      // Il file si scrive INTERO: se queste due non ripartono com'erano, il primo salvataggio
      // dell'editor stati cancellerebbe profili e policy senza dire niente a nessuno.
      roles: parts.roles ?? current.roles ?? [],
      roleAccess: parts.roleAccess ?? current.roleAccess ?? null
    }
    // Un cfg.json senza azioni o senza profili non deve guadagnare una chiave vuota
    if (!payload.aiActions.length) delete payload.aiActions
    if (!payload.roles.length) delete payload.roles
    if (!payload.roleAccess) delete payload.roleAccess
    return await saveCfg(payload)
  }

  // Salva le istruzioni HTML per stato (instructions.json). Solo superadmin lato Backend.
  async function saveInstructions(instructionsConfig) {
    const result = await apiCall('/api/projects/configs/instructions', {
      method: 'PUT',
      body: JSON.stringify(instructionsConfig)
    })
    await loadConfigurations()
    return result
  }

  // Catalogo dei tool (dal codice) + configurazione corrente (tools.json). Solo superadmin.
  async function loadToolsConfig() {
    return await apiCall('/api/projects/configs/tools')
  }

  // Salva tools.json. Ricarica le configurazioni: il form legge da lì il checkbox delle azioni tool.
  async function saveTools(toolsPayload) {
    const result = await apiCall('/api/projects/configs/tools', {
      method: 'PUT',
      body: JSON.stringify(toolsPayload)
    })
    await loadConfigurations()
    return result
  }

  // Elenco delle skill, per il selettore delle verifiche AI nell'editor stati:
  // [{ id, sizeBytes, modifiedAt }].
  async function loadSkills() {
    const result = await apiCall('/api/projects/configs/skills')
    return result?.skills || []
  }

  // Testo integrale del file .md di una skill (frontmatter compreso), per l'editor. Solo superadmin.
  async function loadSkill(skillId) {
    return await apiCall(`/api/projects/configs/skills/${encodeURIComponent(skillId)}`)
  }

  // Salva il testo della skill nel file .md della configurazione. Solo superadmin lato Backend.
  // Niente loadConfigurations(): le skill non fanno parte delle configurazioni caricate all'avvio.
  async function saveSkill(skillId, content) {
    return await apiCall(`/api/projects/configs/skills/${encodeURIComponent(skillId)}`, {
      method: 'PUT',
      body: JSON.stringify({ content })
    })
  }

  // Storico di una skill: ogni salvataggio, import o ripristino archivia la versione sostituita.
  // { current: { modifiedAt }, versions: [{ id, savedAt, savedBy, label, origin, length }] }
  async function loadSkillHistory(skillId) {
    return await apiCall(`/api/projects/configs/skills/${encodeURIComponent(skillId)}/history`)
  }

  async function loadSkillVersion(skillId, versionId) {
    return await apiCall(`/api/projects/configs/skills/${encodeURIComponent(skillId)}/history/${versionId}`)
  }

  // Il ripristino archivia a sua volta la versione corrente: si può disfare dallo stesso storico.
  async function restoreSkillVersion(skillId, versionId) {
    return await apiCall(`/api/projects/configs/skills/${encodeURIComponent(skillId)}/history/${versionId}/restore`, {
      method: 'POST'
    })
  }

  // Passaggio delle skill fra database: il file esportato da uno si importa nell'altro.
  // L'import con `apply: false` è solo l'anteprima; con `apply: true` salva gli `ids` scelti.
  async function exportSkills() {
    return await apiCall('/api/projects/configs/skills-export')
  }

  async function importSkills(exported, { apply = false, ids = [] } = {}) {
    return await apiCall('/api/projects/configs/skills-import', {
      method: 'POST',
      body: JSON.stringify({ ...exported, apply, ids })
    })
  }

  // Quanti processi stanno in ogni (tipo, stato): avviso prima di eliminare o rinumerare uno stato.
  async function loadStatusCounts() {
    const result = await apiCall('/api/projects/status-counts')
    return result?.counts || []
  }

  async function loadProjects(page = 1, status = null, silent = false) {
    try {
      if (!silent) {
        loading.value = true
      }
      error.value = null
      
      // Carica sempre tutti i progetti senza filtro status sul server
      // Il filtro status verrà applicato solo localmente tramite computed
      let endpoint = `/api/projects?page=1&pageSize=1000`
      
      const data = await apiCall(endpoint)

      projects.value = data.projects
      allProjects.value = data.projects // Usa la stessa lista per i summary
      currentPage.value = 1 // Sempre pagina 1 dato che carichiamo tutto
    } catch (err) {
      if (!silent) {
        error.value = 'Erro ao carregar projetos'
      }
      console.error('Erro ao carregar projetos:', err)
    } finally {
      if (!silent) {
        loading.value = false
      }
    }
  }

  async function getProject(projectId) {
    try {
      loading.value = true
      const data = await apiCall(`/api/projects/${projectId}`)
      currentProject.value = data
      return data
    } catch (err) {
      error.value = 'Erro ao carregar projeto'
      console.error('Erro ao carregar projeto:', err)
      return null
    } finally {
      loading.value = false
    }
  }

  // --- Analisi documenti: I/O file via LocalBackend, esecuzione via Backend ---

  // Elenca i file della cartella del progetto (LocalBackend).
  async function listProjectFiles(projectId) {
    const root = await fetchRootPath()
    if (!root) throw new Error('Caminho raiz não configurado')
    const res = await apiCall('/api/projectfolders/list-files', {
      method: 'POST',
      body: JSON.stringify({ basePath: processesDir(root), projectId }),
      backend: 'local',
      includeAuth: false
    })
    return res?.files || []
  }

  // Allinea i checkbox "fileMatch" (quelli che dicono se un documento c'è nella cartella) leggendo
  // la cartella dal LocalBackend e facendo valutare i pattern al Backend, che è l'unico a conoscere
  // Classes/fields. Si mandano solo i path: nome ed estensione li ricava il Backend, e una cartella
  // con migliaia di file passerebbe altrimenti centinaia di KB di metadati inutili.
  //
  // Gli errori si propagano: decide il chiamante se sono un problema (per il form non lo sono, il
  // LocalBackend può semplicemente essere spento).
  async function syncFileChecks(projectId) {
    const files = await listProjectFiles(projectId)
    const res = await apiCall(`/api/projects/${projectId}/file-checks`, {
      method: 'POST',
      body: JSON.stringify({ paths: files.map(f => f.relativePath || f.name).filter(Boolean) })
    })

    // L'eco della propria scrittura la scarta handlers.js confrontando originConnectionId: chi ha
    // fatto la chiamata NON riceve l'evento, quindi la sua riga in dashboard va aggiornata qui.
    if (res?.changed && Object.keys(res.changed).length) await syncOne(projectId)
    return res
  }

  // Legge un file della cartella progetto → { name, relativePath, mimeType, sizeBytes, base64 }.
  async function readProjectFile(projectId, relativePath) {
    const root = await fetchRootPath()
    if (!root) throw new Error('Caminho raiz não configurado')
    return await apiCall('/api/projectfolders/read-file', {
      method: 'POST',
      body: JSON.stringify({ basePath: processesDir(root), projectId, relativePath }),
      backend: 'local',
      includeAuth: false
    })
  }

  // --- Cartella "Documentos" (condivisa, sorella di Processos): fonti standard usabili da qualsiasi
  //     skill (es. regole di beneficio ICMS). Non è per-progetto: la si tratta come un "progetto" il cui
  //     id è il nome della cartella, così list-files/read-file del LocalBackend funzionano invariati
  //     (FindProjectFolder cerca basePath/<id> con match esatto). Ritorna [] se non configurata. ---
  async function listDocuments() {
    const name = folders.value.documents
    if (!name) return []
    const root = await fetchRootPath()
    if (!root) return []
    try {
      const res = await apiCall('/api/projectfolders/list-files', {
        method: 'POST',
        body: JSON.stringify({ basePath: root, projectId: name }),
        backend: 'local',
        includeAuth: false
      })
      // Tag ogni file: `source:'documents'` per il routing in lettura; `relativePath` prefissato col
      // nome cartella così è univoco nella lista del giro 1 e leggibile dal modello; `readPath` resta
      // relativo alla cartella condivisa (per readDocument).
      return (res?.files || []).map(f => ({
        ...f,
        source: 'documents',
        readPath: f.relativePath,
        relativePath: `${name}/${f.relativePath}`
      }))
    } catch {
      return [] // cartella assente o vuota: la navigazione dei documenti condivisi è opzionale
    }
  }

  // Legge un file della cartella condivisa "Documentos" (relativePath relativo a quella cartella).
  async function readDocument(relativePath) {
    const name = folders.value.documents
    if (!name) throw new Error('Cartella "Documentos" non configurata')
    const root = await fetchRootPath()
    if (!root) throw new Error('Caminho raiz não configurado')
    return await apiCall('/api/projectfolders/read-file', {
      method: 'POST',
      body: JSON.stringify({ basePath: root, projectId: name, relativePath }),
      backend: 'local',
      includeAuth: false
    })
  }

  // Salva un file (report) nella cartella progetto (LocalBackend). No-overwrite lato server.
  // encoding: 'text' (default) per report testuali, 'base64' per binari (es. .docx/.xlsx generati).
  async function saveProjectFile(projectId, relativePath, content, encoding = 'text') {
    const root = await fetchRootPath()
    if (!root) throw new Error('Caminho raiz não configurado')
    return await apiCall('/api/projectfolders/save-file', {
      method: 'POST',
      body: JSON.stringify({ basePath: processesDir(root), projectId, relativePath, content, encoding }),
      backend: 'local',
      includeAuth: false
    })
  }

  // --- Varianti streaming (SSE): `onEvent` riceve i VerifyEvent in tempo reale
  //     ({ type: 'phase'|'trace'|'delta'|'result'|'error', ... }). `signal` per annullare (Stop). ---

  // Motore backend (engine:"backend" o assente): una sola chiamata, senza triage. Il loop gira nel
  // Backend (DeepSeek) e gli allegati si mandano sempre con il contenuto.
  // `stepId` valorizzato solo nelle verifiche a più step: il Backend risolve la skill dello step
  // invece che quella della verifica.
  // `runToken` è il token del blocco preso all'avvio: il Backend rifiuta la chiamata se la
  // verifica è in carico a un altro utente.
  async function verifyBackendStream(projectId, verificationId, attachments, onEvent, signal, stepId = null, runToken = null) {
    return await streamCall(`/api/projects/${projectId}/verify-backend/stream`,
      { verificationId, stepId, attachments, runToken }, onEvent, { signal })
  }

  // Motore tool (engine:"tool"): nessun modello e nessun file, il Backend chiama il servizio esterno
  // configurato in tools.json e scrive i campi. Stesso contratto SSE delle verifiche.
  async function runToolStream(projectId, verificationId, onEvent, signal, runToken = null) {
    return await streamCall(`/api/projects/${projectId}/run-tool/stream`,
      { verificationId, runToken }, onEvent, { signal })
  }

  // --- Blocco di una verifica in corso: impedisce che due utenti lancino la stessa verifica sullo
  //     stesso processo, e alimenta l'indicatore delle esecuzioni attive in app bar. ---

  // Prende in carico la verifica. Errore con status 409 quando è già di un altro utente.
  async function acquireVerificationLock(projectId, verificationId, label, connectionId = null) {
    return await apiCall(
      `/api/projects/${projectId}/verification-lock/${encodeURIComponent(verificationId)}`,
      { method: 'POST', body: JSON.stringify({ label, connectionId }) })
  }

  // Battito: tiene vivo il blocco (scade dopo 3 minuti di silenzio) e pubblica lo step corrente.
  // `connectionId` e' quella dell'hub: se cade, il Backend mette il blocco in scadenza breve invece
  // di aspettare i tre minuti pieni. Cambia a ogni riconnessione, quindi si rimanda ogni volta.
  async function beatVerificationLock(projectId, verificationId, token, stepId, stepIndex, stepCount, connectionId = null) {
    return await apiCall(
      `/api/projects/${projectId}/verification-lock/${encodeURIComponent(verificationId)}`,
      { method: 'PUT', body: JSON.stringify({ token, stepId, stepIndex, stepCount, connectionId }) })
  }

  // Rilascio a fine esecuzione. `keepalive` per poterlo chiamare mentre la pagina si chiude.
  async function releaseVerificationLock(projectId, verificationId, token, { keepalive = false } = {}) {
    return await apiCall(
      `/api/projects/${projectId}/verification-lock/${encodeURIComponent(verificationId)}?token=${encodeURIComponent(token)}`,
      { method: 'DELETE', keepalive })
  }

  // Sblocco forzato di un'esecuzione altrui: solo admin/superadmin lato Backend.
  async function forceReleaseVerificationLock(projectId, verificationId) {
    return await apiCall(
      `/api/projects/${projectId}/verification-lock/${encodeURIComponent(verificationId)}`,
      { method: 'DELETE' })
  }

  // Tutte le verifiche in corso, di chiunque.
  async function loadActiveVerificationLocks() {
    const res = await apiCall('/api/projects/verification-locks/active')
    return res?.runs || []
  }

  // Azzera l'avanzamento di una verifica a più step. Senza `stepIds` la azzera tutta e il prossimo
  // clic riparte dal primo; con la lista, solo quegli step — è così che si riparte da uno step
  // qualsiasi, passando quello scelto più tutti i successivi. Ritorna il progetto aggiornato.
  async function clearVerificationProgress(projectId, verificationId, stepIds = null) {
    const query = stepIds?.length ? `?steps=${encodeURIComponent(stepIds.join(','))}` : ''
    return await apiCall(
      `/api/projects/${projectId}/verification-progress/${encodeURIComponent(verificationId)}${query}`,
      { method: 'DELETE' })
  }

  // Azzera i tentativi consumati (limite maxRuns). Endpoint riservato ad admin e superadmin:
  // il "ricomincia" qui sopra azzera gli step ma non restituisce tentativi.
  async function clearVerificationRuns(projectId, verificationId) {
    return await apiCall(
      `/api/projects/${projectId}/verification-runs/${encodeURIComponent(verificationId)}`,
      { method: 'DELETE' })
  }

  // Libera uno step bloccato: la sequenza riparte da quello dopo. Solo admin/superadmin lato
  // Backend, motivo obbligatorio. Ritorna il processo aggiornato (campi di validazione compresi).
  async function overrideVerificationStep(projectId, verificationId, stepId, note) {
    return await apiCall(
      `/api/projects/${projectId}/verification-steps/${encodeURIComponent(verificationId)}/${encodeURIComponent(stepId)}/override`,
      { method: 'POST', body: JSON.stringify({ note }) })
  }

  // Carica le deadline attive (lista slim, tutti i progetti non archiviati, senza paginazione).
  // Usata dal segnale globale in app bar: deve essere disponibile in qualsiasi schermata.
  async function loadDeadlines() {
    try {
      deadlines.value = await apiCall('/api/projects/deadlines')
    } catch (err) {
      console.error('Erro ao carregar deadlines:', err)
    }
  }

  // ---- sincronizzazione mirata (eventi dell'hub) -------------------------------------------
  //
  // L'hub manda un segnale, non i dati: il processo si rilegge da GET /api/projects/{id}, che
  // applica la stessa policy di visibilita' della lista. Cosi' la regola su chi vede cosa resta in
  // un posto solo, invece di essere riscritta anche nel canale in tempo reale.

  function hasProject(projectId) {
    return projects.value.some(p => p.idProject === projectId)
  }

  function versionOf(projectId) {
    return projects.value.find(p => p.idProject === projectId)?.version ?? null
  }

  function removeProject(projectId) {
    projects.value = projects.value.filter(p => p.idProject !== projectId)
    allProjects.value = allProjects.value.filter(p => p.idProject !== projectId)
    if (currentProject.value?.idProject === projectId) currentProject.value = null
  }

  /**
   * Sostituisce (o inserisce) una riga. `currentProject` si tocca solo se non c'e' un form aperto
   * con modifiche non salvate: sovrascriverlo cancellerebbe quello che l'utente sta scrivendo.
   */
  function upsertProject(project) {
    if (!project?.idProject) return
    const id = project.idProject

    const i = projects.value.findIndex(p => p.idProject === id)
    if (i >= 0) projects.value[i] = project
    else projects.value.unshift(project)

    const j = allProjects.value.findIndex(p => p.idProject === id)
    if (j >= 0) allProjects.value[j] = project
    else allProjects.value.unshift(project)

    if (currentProject.value?.idProject === id && !currentProjectDirty.value) {
      currentProject.value = project
    }
  }

  /**
   * Rilettura autoritativa di UN processo. Un 404 qui non significa "sparito" ma "non piu' visibile
   * a questo ruolo": succede quando un collega lo porta in una fase che non ci compete e la policy
   * e' `hidden`. In entrambi i casi la riga se ne va.
   */
  /** Un collega ha toccato il processo che abbiamo aperto e non salvato: lo si dice, non si riscrive. */
  function noteExternalChange(actor) {
    externalChange.value = { actor: actor || '?', at: Date.now() }
  }

  function clearExternalChange() {
    externalChange.value = null
  }

  async function syncOne(projectId) {
    try {
      const fresh = await apiCall(`/api/projects/${projectId}`)
      upsertProject(fresh)
      return fresh
    } catch (err) {
      const status = err?.response?.status
      if (status === 404 || status === 403) {
        removeProject(projectId)
        return null
      }
      console.error('Sincronizzazione live fallita per', projectId, err)
      return null
    }
  }

  async function createProject(projectData) {
    try {
      loading.value = true
      error.value = null
      
      const newProject = await apiCall('/api/projects', {
        method: 'POST',
        body: JSON.stringify(projectData)
      })
      
      // Cria la cartella del progetto nel file system locale PRIMA di aggiungere alla lista
      try {
        await createProjectFolder(newProject.idProject, projectData.client, projectData.projectType)
      } catch (folderError) {
        // Se la creazione della cartella fallisce, elimina il progetto dal backend
        try {
          await apiCall(`/api/projects/${newProject.idProject}`, {
            method: 'DELETE'
          })
        } catch (deleteError) {
          console.error('Failed to rollback project creation:', deleteError)
        }
        throw new Error('Falha ao criar a pasta do projeto: ' + (folderError.message || 'Erro desconhecido'))
      }
      
      // Salva info.txt nella cartella del progetto
      try {
        await saveProjectInfoFile(newProject)
      } catch (infoError) {
        console.warn('Failed to save info.txt:', infoError)
        // Non blocchiamo per questo errore
      }
      
      projects.value.unshift(newProject)
      allProjects.value.unshift(newProject)
      loadDeadlines() // refresh segnale deadline (fire-and-forget)

      return newProject
    } catch (err) {
      error.value = 'Erro ao criar projeto'
      console.error('Erro ao criar projeto:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  async function createProjectFolder(projectId, clientName, projectType) {
    const root = await fetchRootPath()
    if (!root) {
      throw new Error('Caminho raiz não configurado')
    }

    // Chiama il LocalBackend: cartella sotto Processos, template per tipo
    const result = await apiCall('/api/projectfolders/create', {
      method: 'POST',
      body: JSON.stringify({
        basePath: processesDir(root),
        projectId: projectId,
        clientName: clientName || '',
        templatePath: templateDir(root, projectType)
      }),
      backend: 'local',
      includeAuth: false
    })

    console.log('Project folder created:', result)
    return result
  }

  async function openProjectFolder(projectId) {
    try {
      const root = await fetchRootPath()
      if (!root) {
        throw new Error('Root path not configured')
      }

      const result = await apiCall('/api/projectfolders/open', {
        method: 'POST',
        body: JSON.stringify({
          basePath: processesDir(root),
          projectId: projectId
        }),
        backend: 'local',
        includeAuth: false
      })

      return result
    } catch (err) {
      console.error('Error opening project folder:', err)
      throw err
    }
  }

  async function openArchivedProjectFolder(projectId) {
    try {
      const root = await fetchRootPath()
      if (!root) {
        throw new Error('Root path not configured')
      }

      const result = await apiCall('/api/projectfolders/open', {
        method: 'POST',
        body: JSON.stringify({
          basePath: archiveDir(root),
          projectId: projectId
        }),
        backend: 'local',
        includeAuth: false
      })

      return result
    } catch (err) {
      console.error('Error opening archived project folder:', err)
      throw err
    }
  }

  async function checkProjectFolder(projectId) {
    try {
      const root = await fetchRootPath()
      if (!root) {
        throw new Error('Root path not configured')
      }

      const basePath = processesDir(root)
      const result = await apiCall(`/api/projectfolders/check?basePath=${encodeURIComponent(basePath)}&projectId=${encodeURIComponent(projectId)}`, {
        method: 'GET',
        backend: 'local',
        includeAuth: false
      })

      return result.exists
    } catch (err) {
      console.error('Error checking project folder:', err)
      return false
    }
  }

  async function saveProjectInfoFile(projectData) {
    try {
      const root = await fetchRootPath()
      if (!root) {
        console.warn('Root path not configured, skipping info.txt save')
        return
      }
      const localPath = processesDir(root)

      // Converte i dati del progetto in JSON formattato
      const jsonData = JSON.stringify(projectData, null, 2)

      // Chiama il LocalBackend per salvare info.txt
      await apiCall('/api/projectfolders/save-info', {
        method: 'POST',
        body: JSON.stringify({
          basePath: localPath,
          projectId: projectData.idProject,
          jsonData: jsonData
        }),
        backend: 'local',
        includeAuth: false
      })

      console.log('Project info.txt saved successfully')
    } catch (err) {
      console.warn('Warning: Could not save info.txt to local folder. LocalBackend may be offline.', err)
      // Non bloccare l'operazione, solo mostra un warning
    }
  }

  async function updateProject(projectId, projectData) {
    try {
      loading.value = true
      error.value = null
      
      const updatedProject = await apiCall(`/api/projects/${projectId}`, {
        method: 'PUT',
        body: JSON.stringify(projectData)
      })
      
      const index = projects.value.findIndex(p => p.idProject === projectId)
      if (index !== -1) {
        projects.value[index] = updatedProject
      }
      const allIndex = allProjects.value.findIndex(p => p.idProject === projectId)
      if (allIndex !== -1) {
        allProjects.value[allIndex] = updatedProject
      }

      if (currentProject.value && currentProject.value.idProject === projectId) {
        currentProject.value = updatedProject
      }
      
      // Salva info.txt nella cartella del progetto
      await saveProjectInfoFile(updatedProject)
      loadDeadlines() // refresh segnale deadline (fire-and-forget)

      return updatedProject
    } catch (err) {
      // Check if it's a concurrency conflict
      if (err.response?.status === 409 || err.error === 'CONCURRENCY_CONFLICT') {
        const concurrencyError = new Error(err.message || 'Conflitto di concorrenza: il progetto è stato modificato da un altro utente.')
        concurrencyError.isConcurrencyConflict = true
        concurrencyError.details = err
        throw concurrencyError
      }

      // Fase di un altro profilo: chi salva non lavora lo stato in cui il processo si trova.
      if (err.response?.status === 403 || err.error === 'ROLE_SCOPE') {
        const scopeError = new Error(err.message || 'Esta fase do processo é reservada a outros perfis.')
        scopeError.isRoleScope = true
        scopeError.details = err
        throw scopeError
      }

      // Gate di validazione: mancano validazioni obbligatorie per cambiare stato.
      if (err.response?.status === 422 || err.error === 'VALIDATION_GATE') {
        const gateError = new Error(err.message || 'Validações pendentes antes de mudar de estado.')
        gateError.isValidationGate = true
        gateError.pending = err.response?.data?.pending || []
        gateError.details = err
        throw gateError
      }

      error.value = 'Erro ao atualizar projeto'
      console.error('Erro ao atualizar projeto:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  // Set manuale (superadmin) di un campo di validazione checkbox. Ritorna il progetto aggiornato.
  async function setValidationField(projectId, field, value = true) {
    const updated = await apiCall(`/api/projects/${projectId}/validations`, {
      method: 'POST',
      body: JSON.stringify({ field, value })
    })

    const index = projects.value.findIndex(p => p.idProject === projectId)
    if (index !== -1) projects.value[index] = updated
    const allIndex = allProjects.value.findIndex(p => p.idProject === projectId)
    if (allIndex !== -1) allProjects.value[allIndex] = updated
    if (currentProject.value && currentProject.value.idProject === projectId) {
      currentProject.value = updated
    }
    return updated
  }

  async function deleteProject(projectId) {
    try {
      loading.value = true
      error.value = null
      
      await apiCall(`/api/projects/${projectId}`, {
        method: 'DELETE'
      })
      
      projects.value = projects.value.filter(p => p.idProject !== projectId)
      allProjects.value = allProjects.value.filter(p => p.idProject !== projectId)

      if (currentProject.value && currentProject.value.idProject === projectId) {
        currentProject.value = null
      }
    } catch (err) {
      error.value = 'Erro ao deletar projeto'
      console.error('Erro ao deletar projeto:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  async function getProjectHistory(projectId) {
    try {
      loading.value = true
      error.value = null
      
      const history = await apiCall(`/api/projects/${projectId}/history`)
      return history
    } catch (err) {
      error.value = 'Erro ao carregar histórico do projeto'
      console.error('Erro ao carregar histórico:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  async function archiveProject(projectId) {
    try {
      loading.value = true
      error.value = null
      
      // Passo 1: Ottieni la root configurata
      const root = await fetchRootPath()
      if (!root) {
        throw new Error('Caminho raiz não configurado')
      }

      // Passo 2: Sposta la cartella da Processos ad Arquivo tramite LocalBackend
      try {
        await apiCall('/api/projectfolders/archive', {
          method: 'POST',
          body: JSON.stringify({
            basePath: processesDir(root),
            projectId: projectId,
            archiveBasePath: archiveDir(root)
          }),
          backend: 'local',
          includeAuth: false
        })
      } catch (folderError) {
        throw new Error('Falha ao mover a pasta do projeto para o arquivo: ' + (folderError.message || 'Erro desconhecido'))
      }
      
      console.log('Project folder archived in local backend')
      
      // Passo 3: Archivia il progetto nel Backend (sposta JSON)
      try {
        await apiCall(`/api/projects/${projectId}/archive`, {
          method: 'POST'
        })
      } catch (backendError) {
        // Se fallisce l'archiviazione nel backend, potremmo voler fare rollback della cartella
        // ma per ora solo loghiamo l'errore
        console.error('Failed to archive project in backend after moving folder:', backendError)
        throw new Error('Falha ao arquivar projeto no servidor: ' + (backendError.message || 'Erro desconhecido'))
      }
      
      console.log('Project archived in backend')
      
      // Rimuovi il progetto dalle liste (projects + allProjects per i summary della dashboard)
      projects.value = projects.value.filter(p => p.idProject !== projectId)
      allProjects.value = allProjects.value.filter(p => p.idProject !== projectId)
      loadDeadlines() // refresh segnale deadline (fire-and-forget)

      if (currentProject.value && currentProject.value.idProject === projectId) {
        currentProject.value = null
      }

      return { success: true }
    } catch (err) {
      error.value = err.message || 'Erro ao arquivar projeto'
      console.error('Erro ao arquivar projeto:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  function setStatusFilter(status) {
    statusFilter.value = status
    // Non ricarichiamo più i progetti, solo cambiamo il filtro
    // che verrà applicato tramite filteredProjects computed
  }

  // Ambito per profilo. Si passa solo cio' che cambia: { onlyMine } dal toggle della tabella,
  // { role } dal selettore utente degli admin.
  function setScopeFilter(patch) {
    scopeFilter.value = { ...scopeFilter.value, ...patch }
  }

  function setGroupFilter(group) {
    groupFilter.value = group
  }

  function setProjectTypeFilter(projectType) {
    projectTypeFilter.value = projectType
  }

  function clearError() {
    error.value = null
  }

  function clearCurrentProject() {
    currentProject.value = null
  }

  // Verificar se campo é obrigatório para um status específico
  function isFieldRequired(fieldName, statusNumber) {
    const field = fieldRules.value[fieldName]
    if (!field) return false
    // Se mandatoryStatusNumber não está definido, o campo não é obrigatório
    if (field.mandatoryStatusNumber === undefined) return false
    return field.mandatoryStatusNumber <= statusNumber
  }

  // Verificar se campo é visível para um status específico
  function isFieldVisible(fieldName, statusNumber) {
    const field = fieldRules.value[fieldName]
    if (!field) return true
    // Se visibleInStatusNumber não está definido, o campo é sempre visível
    if (field.visibleInStatusNumber === undefined) return true
    return field.visibleInStatusNumber <= statusNumber
  }

  // Verificar se campo é readonly
  function isFieldReadonly(fieldName) {
    const field = fieldRules.value[fieldName]
    if (!field) return false
    return field.readonly === true
  }

  // Obter informações do status por ID
  function getStatusInfo(statusId, projectTypeId = null) {
    if (statusId === null || statusId === undefined) return null
    
    // Se specificato il tipo, cerca negli stati di quel tipo
    if (projectTypeId !== null && projectTypeId !== undefined) {
      const projectType = projectTypes.value.find(pt => pt.id.toString() === projectTypeId.toString())
      const statusInfo = projectType?.statusList?.find(status => status.id.toString() === statusId.toString())
      
      // Se lo stato ha instructionKey, aggiungi le instructions dal file separato
      if (statusInfo && statusInfo.instructionKey) {
        const [typeKey, statusKey] = statusInfo.instructionKey.split('.')
        statusInfo.instructions = instructions.value[typeKey]?.[statusKey] || ''
      }
      
      return statusInfo
    }
    // Altrimenti cerca in tutti gli stati
    const statusInfo = statusList.value.find(status => status.id.toString() === statusId.toString())
    
    // Se lo stato ha instructionKey, aggiungi le instructions dal file separato
    if (statusInfo && statusInfo.instructionKey) {
      const [typeKey, statusKey] = statusInfo.instructionKey.split('.')
      statusInfo.instructions = instructions.value[typeKey]?.[statusKey] || ''
    }
    
    return statusInfo
  }

  // Obter lista de status para um tipo de projeto específico
  function getStatusListForType(projectTypeId) {
    if (projectTypeId === null || projectTypeId === undefined) return []
    const projectType = projectTypes.value.find(pt => pt.id.toString() === projectTypeId.toString())
    return projectType?.statusList || []
  }

  // Obter informações do tipo de projeto por ID
  function getProjectTypeInfo(typeId) {
    if (typeId === null || typeId === undefined) return null
    return projectTypes.value.find(type => type.id.toString() === typeId.toString())
  }

  return {
    // Estado
    projects,
    allProjects,
    loading,
    error,
    currentProject,
    openProjectId,
    currentProjectDirty,
    externalChange,
    archiveTick,
    statusFilter,
    projectTypeFilter,
    groupFilter,
    scopeFilter,
    configurations,
    currentPage,
    pageSize,
    totalProjects,
    
    // Computed
    currentRole,
    onlyMyStatuses,
    scopedProjects,
    filteredProjects,
    workflowRoles,
    roleAccessPolicy,
    statusList,
    projectTypes,
    aiActions,
    toolsConfig,
    fields,
    fieldsByProjectType,
    fieldRules,
    instructions,
    tableColumns,
    
    // State
    deadlines,

    // Computed
    folders,

    // Actions
    loadConfigurations,
    saveFieldsByType,
    saveColumns,
    saveCfg,
    saveCfgParts,
    resolveVerifications,
    saveInstructions,
    loadSkills,
    loadSkill,
    saveSkill,
    exportSkills,
    importSkills,
    loadSkillHistory,
    loadSkillVersion,
    restoreSkillVersion,
    loadStatusCounts,
    loadProjects,
    loadDeadlines,
    getProject,
    hasProject,
    versionOf,
    removeProject,
    upsertProject,
    syncOne,
    noteExternalChange,
    clearExternalChange,
    listProjectFiles,
    syncFileChecks,
    readProjectFile,
    listDocuments,
    readDocument,
    saveProjectFile,
    verifyBackendStream,
    runToolStream,
    loadToolsConfig,
    saveTools,
    clearVerificationProgress,
    clearVerificationRuns,
    overrideVerificationStep,
    acquireVerificationLock,
    beatVerificationLock,
    releaseVerificationLock,
    forceReleaseVerificationLock,
    loadActiveVerificationLocks,
    createProject,
    updateProject,
    setValidationField,
    deleteProject,
    getProjectHistory,
    archiveProject,
    setStatusFilter,
    setProjectTypeFilter,
    setGroupFilter,
    setScopeFilter,
    clearError,
    clearCurrentProject,
    createProjectFolder,
    openProjectFolder,
    openArchivedProjectFolder,
    checkProjectFolder,
    saveProjectInfoFile,
    
    // Helpers
    isFieldRequired,
    isFieldVisible,
    isFieldReadonly,
    getStatusInfo,
    getStatusListForType,
    getProjectTypeInfo
  }
})